import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useEraseCustomer } from "../hooks/useCustomers";
import { showToast } from "../lib/toast";
import { getErrorMessage } from "../lib/apiErrors";

interface CustomerEraseDialogProps {
  customerId: string;
  customerName: string;
  customerEmail: string;
  // Tickets they requested (trashed ones aren't counted) - all of them are permanently deleted
  ticketCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Erases an archived customer's personal data (GDPR right to erasure) - Admins of the customer's organization. The
 * admin types the customer's email address to confirm. Irreversible.
 */
export const CustomerEraseDialog: React.FC<CustomerEraseDialogProps> = ({
  customerId,
  customerName,
  customerEmail,
  ticketCount,
  open,
  onOpenChange,
}) => {
  const [confirmEmail, setConfirmEmail] = useState("");
  const eraseMutation = useEraseCustomer();
  const matches = confirmEmail.trim().toLowerCase() === customerEmail.trim().toLowerCase();

  const handleOpenChange = (next: boolean) => {
    if (!next) setConfirmEmail("");
    onOpenChange(next);
  };

  const handleErase = async () => {
    try {
      const result = await eraseMutation.mutateAsync({ customerId, confirmEmail: confirmEmail.trim() });
      showToast.success(
        "Personal Data Erased",
        `${result.ticketsDeleted} ticket${result.ticketsDeleted === 1 ? "" : "s"} deleted and ${result.messagesRemoved} message${result.messagesRemoved === 1 ? "" : "s"} removed.`
      );
      handleOpenChange(false);
    } catch (error) {
      showToast.error("Erase Failed", getErrorMessage(error, "Failed to erase the customer's data. Please try again."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Erase {customerName}'s personal data?</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>For a right-to-erasure request. This can't be undone:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Every ticket they requested ({ticketCount}, plus any in the trash) is permanently deleted, with
                  its comments, history and files.
                </li>
                <li>Their messages on other tickets are removed and their files deleted.</li>
                <li>Emails to and from them are deleted.</li>
                <li>Their name and email are replaced everywhere with "Erased customer".</li>
              </ul>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="erase-confirm-email">
            Type <span className="font-medium text-foreground">{customerEmail}</span> to confirm
          </Label>
          <Input
            id="erase-confirm-email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleErase}
            disabled={!matches || eraseMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {eraseMutation.isPending ? "Erasing..." : "Erase Personal Data"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
